import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import Login from "../views/LoginView.vue";
import SetPassword from "../views/SetPassword.vue";
import About from "../views/AboutView.vue";
import NotFound from "../views/NotFound.vue";
import { AuthModule } from "@/store/modules/auth";
import { BreadcrumbModule } from "@/store/modules/breadcrumbs";
import {
  assessmentTableBreadcrumbGenerator,
  assessmentDetailBreadcrumbGenerator,
  assignmentBreadcrumbGenerator,
  assignmentDetailBreadcrumbGenerator,
  breadcrumbsUntilCourseDetail,
  courseForumBreadcrumbGenerator,
  submissionBreadcrumbGenerator,
  videoListBreadcrumbGenerator,
  quizzesBreadcrumbGenerator,
} from "@/router/breadcrumbs";
import { assessmentAnalysisApi } from "@/api/ilect-openapi-configuration";
import PasswordChange from "@/views/PasswordChange.vue";

// Type for route record is exported in @types

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: {
      title: "iLect-CL Login",
      breadcrumbGenerator: undefined,
      requiresAuth: undefined,
    },
  },

  {
    path: "/set-password",
    name: "SetPassword",
    component: SetPassword,
    meta: {
      title: "iLect-CL Set Password",
    },
  },

  {
    path: "/password-change",
    name: "PasswordChange",
    component: PasswordChange,
    meta: {
      title: "Change Password",
    },
  },

  {
    path: "/about",
    name: "About",
    component: About,
    meta: {
      title: "iLect-CL About",
    },
  },

  {
    path: "/profile/:userId?",
    name: "Profile",
    component: () => import("../views/ProfileView.vue"),
    meta: {
      title: "iLect-CL Profile",
      requiresAuth: true,
    },
  },

  {
    path: "/progress",
    name: "Progress",
    component: () => import("../views/ProgressView.vue"),
    meta: {
      title: "Learning Progress",
      requiresAuth: true,
    },
  },

  {
    path: "/gke/stats/legacy",
    name: "WorkbookGkeStats",
    component: () => import("../views/GkeStats.vue"),
    beforeEnter: (to, from, next) => {
      if (AuthModule.isStaff()) {
        next();
      } else {
        next(from);
      }
    },
    meta: {
      title: "GKE Stats V1",
      requiresAuth: true,
    },
  },

  {
    path: "/gke/stats",
    name: "WorkbookGkeStatsV2",
    component: () => import("../views/GkeStatsV2.vue"),
    beforeEnter: (to, from, next) => {
      if (AuthModule.isStaff()) {
        next();
      } else {
        next(from);
      }
    },
    meta: {
      title: "GKE Stats V2",
      requiresAuth: true,
    },
  },

  {
    path: "/gke/sessions",
    name: "GkeSessions",
    component: () => import("../views/GkeSessionsView.vue"),
    beforeEnter: (to, from, next) => {
      if (AuthModule.isStaff()) {
        next();
      } else {
        next(from);
      }
    },
    meta: {
      title: "GKE Sessions",
      requiresAuth: true,
    },
  },

  {
    path: "/course",
    name: "Course",
    component: () => import("../views/CourseView.vue"),
    meta: {
      title: "iLect-CL Course",
      requiresAuth: true,
    },
  },

  {
    path: "/course/:course_id/workbook",
    name: "Workbook",
    component: () => import("../views/WorkbookView.vue"),
    meta: {
      title: "iLect Workbook",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          {
            label: "Workbook",
            to: null,
            icon: "fas fa-book-open",
          },
        ];
      },
    },
  },

  {
    path: "/course/:course_id/calendar",
    name: "Calendar",
    component: () => import("../views/CalendarView.vue"),
    meta: {
      title: "Calendar",
      requiresAuth: true,
    },
  },

  {
    path: "/course/:course_id/assignment",
    name: "Assignment",
    component: () => import("../views/AssignmentView.vue"),
    meta: {
      title: "iLect-CL Assignment",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          assignmentBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/assessment",
    name: "Assessment",
    beforeEnter: (to, from, next) => {
      const courseId = to.params.course_id as string;
      const userId = AuthModule.userId;
      assessmentAnalysisApi
        .assessmentAnalysisExistenceCourseRetrieve(courseId)
        .then((res) => {
          // if no assessment data exists do not forward request
          if (!res.data) {
            return;
          }
          if (AuthModule.isStaff() || AuthModule.isEditor(courseId)) {
            next();
          } else {
            next({
              name: "AssessmentDetail",
              params: { course_id: courseId, user_id: userId },
            });
          }
        })
        .catch((err: unknown) => {
          console.error(err);
        });
    },
    component: () => import("../views/AssessmentView.vue"),
    meta: {
      title: "iLect-CL Assessment",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          assessmentTableBreadcrumbGenerator(),
        ];
      },
    },
  },
  {
    path: "/course/:course_id/assessment/:user_id/",
    name: "AssessmentDetail",
    beforeEnter: (to, from, next) => {
      const courseId = to.params.course_id as string;

      // check if assessment data is available
      assessmentAnalysisApi
        .assessmentAnalysisExistenceCourseRetrieve(courseId)
        .then((res) => {
          // if no assessment data exists do not forward request
          if (!res.data) {
            return;
          }
          next();
        })
        .catch((err) => {
          console.error(err);
        });
    },
    component: () => import("../views/AssessmentDetail.vue"),
    meta: {
      title: "iLect-CL Assessment Detail",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          assessmentTableBreadcrumbGenerator(),
          assessmentDetailBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/assignment/:assignment_id",
    name: "AssignmentDetail",
    component: () => import("../views/AssignmentDetail.vue"),
    meta: {
      title: "iLect-CL Assignment",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          assignmentBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/submission",
    name: "Submission",
    component: () => import("../views/Submission.vue"),
    meta: {
      title: "iLect-CL Submission",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          submissionBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/submission/:submission_id",
    name: "SubmissionDetail",
    component: () => import("../views/SubmissionDetail.vue"),
    meta: {
      title: "iLect-CL Submission",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          submissionBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/forum-records",
    name: "ForumRecordList",
    component: () => import("../views/ForumRecordListView.vue"),
    meta: {
      title: "Forum Record List",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          courseForumBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/forum-records/:forum_record_id",
    name: "ForumRecordDetail",
    component: () => import("../views/ForumRecordDetailView.vue"),
    meta: {
      title: "Forum Record Detail",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          courseForumBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/forum-records/create",
    name: "ForumRecordCreate",
    component: () => import("../views/ForumRecordCreateView.vue"),
    meta: {
      title: "Forum Record Create",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          courseForumBreadcrumbGenerator(),
        ];
      },
    },
  },

  {
    path: "/course/:course_id/settings",
    name: "Settings",
    component: () => import("../views/SettingsView.vue"),
    meta: {
      title: "iLect-CL Settings",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          {
            label: "Settings",
            to: null,
            icon: "settings",
          },
        ];
      },
    },
  },

  {
    path: "/course/:course_id/quizzes",
    name: "QuizList",
    component: () => import("../views/QuizListView.vue"),
    meta: {
      title: "Quiz list per course",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          {
            label: "Quiz List",
            icon: "Quiz List",
          },
        ];
      },
    },
  },

  {
    path: "/course/:course_id/quizzes/:quiz_id/quiz-courses/:quiz_course_id",
    name: "QuizDetailV2",
    component: () => import("../views/QuizDetailView.vue"),
    meta: {
      title: "Quiz",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          quizzesBreadcrumbGenerator(),
          {
            label: "Quiz",
            icon: "Quiz",
          },
        ];
      },
    },
  },

  {
    path: "/course/:course_id/quizzes/:quiz_id/quiz-submissions/:quiz_submission_id",
    name: "QuizSubmissionDetail",
    component: () => import("../views/QuizSubmissionDetail.vue"),
    meta: {
      title: "QuizSubmission",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          quizzesBreadcrumbGenerator(),
          {
            label: "Quiz Result",
            icon: "Quiz",
          },
        ];
      },
    },
  },

  {
    path: "/course/:course_id/testcase/:assignment_id",
    name: "TestCase",
    component: () => import("../views/TestCase.vue"),
    meta: {
      title: "iLect-CL TestCase",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          assignmentBreadcrumbGenerator(),
          assignmentDetailBreadcrumbGenerator(),
          {
            label: "Testcase",
            icon: "",
          },
        ];
      },
    },
  },

  {
    path: "/privacy-policy",
    name: "PrivacyPolicy",
    component: () => import("../views/PrivacyPolicy.vue"),
    meta: {
      title: "iLect-CL Privacy Policy",
    },
  },

  {
    path: "/",
    name: "Home",
    redirect: "Course",
  },

  {
    path: "/pygrade/",
    name: "PyGrade",
    redirect: "",
    meta: { title: "PyGrade" },
    beforeEnter: () => {
      window.location.href = `${window.location.origin}/pygrade/`;
      return false;
    },
  },

  {
    path: "/:catchAll(.*)",
    name: "NotFound",
    component: NotFound,
    meta: {
      title: "iLect-CL Not Found",
      requiresAuth: true,
    },
  },

  {
    path: "/course/:course_id/video",
    name: "Video",
    component: () => import("../views/VideoList.vue"),
    meta: {
      title: "Video",
      requiresAuth: true,
      breadcrumbGenerator: () => [
        ...breadcrumbsUntilCourseDetail(),
        videoListBreadcrumbGenerator(),
      ],
    },
  },
  {
    path: "/course/:course_id/:platform/:video_url",
    name: "VideoDetail",
    component: () => import("../views/VideoDetail.vue"),
    meta: {
      title: "Video Detail",
      requiresAuth: true,
      breadcrumbGenerator: () => {
        return [
          ...breadcrumbsUntilCourseDetail(),
          videoListBreadcrumbGenerator(),
          {
            label: BreadcrumbModule.getVideoTitle(),
            icon: "",
          },
        ];
      },
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes,
});

// Check whether a requested page requires authentications or not,
// if it does, then check whether a user is authenticated or not.
router.beforeEach((to, from, next) => {
  document.title = to.meta.title;
  if (to.meta.requiresAuth && !AuthModule.isAuthenticated) {
    AuthModule.setCallback(to);
    next("/login");
  } else {
    next();
  }
});

export default router;
